import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/features/auth/AuthProvider";
import { useStaff } from "@/hooks/useStaff";
import { homePathForRoles, type AppRole } from "@/types/roles";

type Props = {
  children: ReactNode;
  requireRoles?: AppRole[];
};

export function AuthGate({ children, requireRoles }: Props) {
  const { user, effectiveRoles, client, loading } = useAuth();
  const location = useLocation();

  const isClient = Boolean(client);
  // Client logins have no staff row — skip the staff query for them entirely
  // (it would return null and, worse, its loading state would stall the gate).
  const { data: staff, isLoading: staffLoading } = useStaff({ enabled: !isClient });

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  // First-login gate. Force a password change before any guarded route. The
  // flag lives on the staff row for staff, and on the client_users row for
  // client logins (surfaced via auth context) — cover both.
  if (!isClient && staffLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }
  const mustChangePassword = isClient
    ? client?.mustChangePassword
    : staff?.must_change_password;
  if (mustChangePassword && location.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }

  // Keep a client login inside /client, and keep everyone else OUT of it.
  if (isClient && !location.pathname.startsWith("/client")) {
    return <Navigate to="/client" replace />;
  }

  if (requireRoles && requireRoles.length > 0) {
    const hasRole = effectiveRoles.some((r) => requireRoles.includes(r));
    if (!hasRole) {
      // Send them to their own home rather than dumping them somewhere they
      // can't be — a client to /client, an admin-ish user to /admin, etc.
      return <Navigate to={homePathForRoles(effectiveRoles)} replace />;
    }
  }

  return <>{children}</>;
}

export function PublicOnly({ children }: { children: ReactNode }) {
  const { user, effectiveRoles, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={homePathForRoles(effectiveRoles)} replace />;
  }
  return <>{children}</>;
}
